// The stored `tasks` row as the domain reads it.
// See `../task.ts` for the file docblock.

import { z } from 'zod';
import { AccountIdSchema, TaskIdSchema } from '../../value-objects/ids';
import { TaskEnvelopeSchema } from '../../value-objects/task-envelope';
import { TaskCancelReason, TaskCancelReasonSchema } from './cancel-reason';
import { TaskFailureKindSchema } from './failure-kind';
import { TaskHoldReasonSchema } from './hold';
import { OutcomeKindSchema } from './outcome';
import { TaskStatus, TaskStatusSchema } from './status';

export const TaskSchema = z.object({
  /** `tsk_<hex>` — the branded id IS the stored text PK (same scheme as accounts). */
  id: TaskIdSchema,
  accountId: AccountIdSchema,
  status: TaskStatusSchema,
  /** What the task delivers — `pr` (default; babysit-watched) | `report` | `ops_action` (doc 04). */
  outcomeKind: OutcomeKindSchema,
  /** Lower runs first, WITHIN a scheduling class. `claimNextReady` orders `class ASC, priority ASC, created_at ASC, id ASC` (slice 06a; `id` added by #1470 to make the order total). */
  priority: z.number().int(),
  /** Sibling task ids that must reach `completed`/`merged` before this is ready. */
  dependsOn: z.array(TaskIdSchema),
  /** Claiming runner (`runners.id`); null until claimed. */
  runnerId: z.string().nullable(),
  /** Execution run id — keys the `fleet.evt.<run_id>` stream; null until started. */
  runId: z.string().nullable(),
  /**
   * Ownership generation — the FENCE TOKEN (arc 101 doc 03). Bumped by every
   * hand-over of the task: each claim (`claimNextReady`) and every revocation
   * (`reapStaleRunner`, `drainRunner`, `reassignTask`, `redispatchTask`).
   * Monotonic per task, never reset. The assigner snapshots it onto the
   * `FleetTaskAssignment`; the box echoes it on every run event; the mothership
   * requires it on the terminal task write — so a partitioned box whose task was
   * re-queued and re-claimed is fenced out at the write instead of clobbering the
   * live run's state. `.default(0)` mirrors the column default: rows minted before
   * the fence landed read as generation 0.
   */
  leaseEpoch: z.number().int().nonnegative().default(0),
  /**
   * Failed attempts so far (arc 101 WK-3) — bumped by every failure lane (the
   * box's terminal `error` event AND the stuck-task reaper's silence re-pend).
   * A failure re-pends while `attempt < TASK_MAX_ATTEMPTS`; at the bound it is
   * terminal. `.default(0)` mirrors the column default (migration 0130).
   */
  attempt: z.number().int().nonnegative().default(0),
  /**
   * Classification of the LAST failure that touched this row (retry or
   * terminal), or null for a task no failure lane ever touched. Stamped on
   * every bump of `attempt`, so a terminal `failed` row says WHY it died and a
   * retried row says what its previous attempt died of.
   */
  failureKind: TaskFailureKindSchema.nullable().default(null),
  /**
   * Why this task is held rather than merely queued (#1899), or null when
   * nothing is holding it. Only ever set while `status` is `pending`: the read
   * derives it against the row's own status, so a reason cannot outlive the wait
   * it describes and be read off a task that has since been claimed.
   */
  holdReason: TaskHoldReasonSchema.nullable().default(null),
  /**
   * When {@link holdReason}'s window ends (`tasks.hold_until`), or null — derived
   * against `status` exactly like the reason, so an instant cannot outlive its wait.
   * Read by the review check, which names it to the pull request's author. The read
   * seam (`normalizeTask`) always sets it; OPTIONAL only in the type, so a Task built
   * by hand without it reads as "no window" rather than failing to compile.
   */
  holdUntil: z.date().nullable().optional(),
  /**
   * WHY the row was last cancelled (`tasks.cancel_reason`, #3339), or null — never
   * recorded, or never cancelled. Read RAW, unlike {@link holdReason}: the column records
   * the row's LAST cancel and survives revival (see {@link TaskCancelReason}), so a reader
   * pairs it with `status` itself — {@link stoppedByOperator} is that pairing for the one
   * question the babysit lane asks of it (#4260).
   */
  cancelReason: TaskCancelReasonSchema.nullable().default(null),
  /**
   * The row's current claim was admitted FOR PLANNING (`tasks.plan_admitted_at`, #5370):
   * no area was taken for it, and the box's editors may write only after `files_plan`
   * locks its plan. What the assigner turns into `assignment.plan_admitted`. OPTIONAL in
   * the type, like {@link holdUntil}: a Task built by hand without it is not admitted.
   */
  planAdmitted: z.boolean().optional(),
  /**
   * The machine this task was SENT to (`tasks.target_runner_id`, #6767): a HARD target, so
   * only that machine may claim it until {@link targetUntil}; null when nobody sent it, or
   * once `cron.target_release` cleared it. Not `preferred_runner_id`, which only reorders.
   * OPTIONAL in the type, like {@link holdUntil}: a Task built by hand without it has none.
   */
  targetRunnerId: z.string().nullable().optional(),
  /** When {@link targetRunnerId}'s hold ends (`tasks.target_until`): send time + 10 min. */
  targetUntil: z.date().nullable().optional(),
  /**
   * `tasks.pull_request_number` — the PR this task OPENED (#543 write-back), null until linked.
   * Never set on a task that acts on an existing PR (review, babysit, reply: barred by
   * `schema/tasks.ts`), so it names a plan leg's own PR. Optional: the rows every fixture
   * builds predate it, and only the queue row reads it (`TaskWireSchema.pull_request_number`).
   */
  pullRequestNumber: z.number().int().positive().nullable().optional(),
  /**
   * The human who dispatched this task (`users.id`) — the claim scope a
   * PRIVATE-to-user box matches. Null for system-dispatched tasks and legacy
   * user-less tokens. Denormalized from `envelope.dispatched_by.user_id`.
   */
  dispatchedByUserId: z.string().nullable(),
  /**
   * The conversation that asked for this task (#1896) — the `conversation_ref`
   * COLUMN, not the envelope path. Both hold the same value (`createTask` derives
   * the column from the envelope), but this is the queryable one, and it is what
   * the scout-plan ingest reads to stamp a plan's children. Null ⇒ the task was
   * not asked for through a conversation. `.default(null)` mirrors the nullable
   * column (migration 0132), the same posture as {@link failureKind}.
   */
  conversationRef: z.string().nullable().default(null),
  /** Dispatch-ready envelope pushed to the runner verbatim. */
  envelope: TaskEnvelopeSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type Task = z.infer<typeof TaskSchema>;

/** The {@link TaskCancelReason}s {@link stoppedByOperator} reads — the SQL twin's input. */
export const OPERATOR_STOP_CANCEL_REASONS: readonly TaskCancelReason[] = [
  TaskCancelReason.OperatorStop,
  TaskCancelReason.PlanStop,
  // The split follow-through is the operator's stop done for them (`cancel-reason.ts`).
  TaskCancelReason.SupersededBySplit,
];

/**
 * Was this row cancelled BY AN OPERATOR — the control-MCP stop of the task or of its
 * whole plan (#4260), or the split follow-through that stops a non-converging PR's task
 * for them? A stopped task's PR is not an orphan to rescue: the babysit
 * dead-owner rescue (`apps/worker/src/babysit/driver.ts` `driveForPr`) and the backfill
 * enumeration (`@developerz/db` `listDeadOwnerOrphanPrs`) both refuse it, or a stop
 * re-anchors a watch that can MERGE the PR the operator stopped. Paired with `status`
 * because `cancelReason` outlives a revival.
 */
export function stoppedByOperator(task: Pick<Task, 'status' | 'cancelReason'>): boolean {
  return (
    task.status === TaskStatus.Cancelled &&
    task.cancelReason !== null &&
    OPERATOR_STOP_CANCEL_REASONS.includes(task.cancelReason)
  );
}
